// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { CancelRateNegotiationBottomSheet } from "@src/appV2/Negotiations/CancelRateNegotiationBottomSheet";
import { RateNegotiationBottomSheet } from "@src/appV2/Negotiations/RateNegotiationBottomSheet";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { ClipboardScoreEducationFlowContainer } from "../ClipboardScore/components/Education/EducationFlowContainer";
import { invalidateShifts } from "../invalidateShifts";
import { ModalRoute } from "../Navigation/ModalRoute";
import { ModalTransitionSwitch } from "../Navigation/ModalTransitionSwitch";
import { BasketSizeWillReachWarning } from "../Shift/BasketSizeWillReachWarning/BasketSizeWillReachWarning";
import { InstantReviewShiftDocumentSubmittedBottomSheet } from "../Shift/Bookability/Documents/InstantReview/DocumentSubmittedBottomSheet";
import { InstantReviewSignFormBottomSheet } from "../Shift/Bookability/Documents/InstantReview/SignFormBottomSheet";
import { LocationPermissionsBottomSheet } from "../Shift/Bookability/LocationPermissionsBottomSheet";
import { ArrivalTimeBottomSheetContainer } from "../Shift/BookingConfirmationBottomSheet/ArrivalTime/SheetContainer";
import { ConfirmBookingBottomSheetContainer } from "../Shift/BookingConfirmationBottomSheet/ConfirmBookingBottomSheetContainer";
import { EducateNewWorkerBottomSheet } from "../Shift/BookingConfirmationBottomSheet/EducateNewWorkerBottomSheet/EducateNewWorkerBottomSheet";
import { ShiftBookedBottomSheetContainer } from "../Shift/BookingConfirmationBottomSheet/ShiftBookedBottomSheetContainer";
import { ShiftBottomSheet } from "../Shift/BottomSheet/Sheet";
import { BreakConfirmationModalRouteContainer } from "../Shift/BreakConfirmationBottomSheet/BreakConfirmationModalRouteContainer";
import { BundleBookedSheet } from "../Shift/Bundle/BottomSheet/BundleBookedSheet";
import { SelectionSheet } from "../Shift/Bundle/BottomSheet/SelectionSheet";
import { ShiftBundleBottomSheet } from "../Shift/Bundle/BottomSheet/Sheet";
import { DoubleBookedSheet } from "../Shift/Double/BottomSheet/DoubleBookedSheet";
import { ShiftDoubleBottomSheet } from "../Shift/Double/BottomSheet/Sheet";
import { ConfirmAuctionInstantBookBottomSheetContainer } from "../Shift/ShiftAuction/ConfirmAuctionInstantBookBottomSheetContainer";
import { SHIFT_AUCTION_BID_PAY_PARAMETER_NAME } from "../Shift/ShiftAuction/constants";
import { PlaceBidBottomSheetContainer } from "../Shift/ShiftAuction/PlaceBidBottomSheetContainer";
import { ReviewBidBottomSheetContainer } from "../Shift/ShiftAuction/ReviewBidBottomSheetContainer";
import {
  SHIFT_DISCOVERY_BASKET_SIZE_WILL_REACH_WARNING_MODAL_PATH,
  SHIFT_DISCOVERY_ENABLE_LOCATION_PERMISSIONS_PATH,
  SHIFT_DISCOVERY_SHIFT_ARRIVAL_TIME_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_AUCTION_CONFIRM_INSTANT_BOOK_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_AUCTION_REVIEW_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BREAK_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BUNDLE_BOOKED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BUNDLE_SELECT_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_EDUCATE_ABOUT_CLIPBOARD_SCORE_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_EDUCATE_NEW_WORKER_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_NEGOTIATION_CANCEL_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH,
} from "./paths";
import { useShiftModalsDataContext } from "./useShiftModalsDataContext";

function getGoBackPath(parameters: {
  baseNavigationPath: string;
  urlSearchParams: URLSearchParams;
}) {
  const { baseNavigationPath, urlSearchParams } = parameters;

  urlSearchParams.delete("rateNegotiationId");

  if (urlSearchParams.get("redirectTo") === "place-bid-modal") {
    urlSearchParams.delete("redirectTo");
  }

  if (urlSearchParams.size > 0) {
    return `${baseNavigationPath}?${urlSearchParams.toString()}`;
  }

  return baseNavigationPath;
}

/**
 * ShiftModalRoutes handles all shift-related modals in the application.
 *
 * Key characteristics:
 * 1. Can be rendered independently or on top of workplace modals
 * 2. Uses baseNavigationPathOverride when rendered on top of workplace modals
 * 3. Shares common transition animations with other modals
 *
 * Common flows:
 * - Direct shift modal: /shift-discovery/list/shift/:id
 * - Nested shift modal: /shift-discovery/list/workplace/:id/open-shifts/shift/:id
 */
export function ShiftModalRoutes() {
  const {
    baseNavigationPath: defaultBaseNavigationPath,
    baseNavigationPathOverride,
    shift,
    bookabilityStatus,
    computedArrivalData,
  } = useShiftModalsDataContext();

  const location = useLocation();

  const isNestedModalContext = isDefined(baseNavigationPathOverride);
  const baseNavigationPath = isNestedModalContext
    ? `${defaultBaseNavigationPath}/${baseNavigationPathOverride}`
    : defaultBaseNavigationPath;

  const queryClient = useQueryClient();

  const urlSearchParams = new URLSearchParams(location.search);
  const rateNegotiationId = urlSearchParams.get("rateNegotiationId") ?? undefined;

  const goBackPath = getGoBackPath({
    baseNavigationPath,
    urlSearchParams,
  });

  useEffect(() => {
    // Invalidate shifts cache when booking confirmation appears.
    // Previously, when this invalidation happened inside the modal component,
    // it would be cancelled during modal unmounting, causing UI inconsistencies.
    // If you closed the modal quick enough, list would not be updated, showing your booked shifts as open (but not bookable)
    if (
      isDefined(
        matchPath(
          location.pathname,
          `${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH}`
        )
      ) ||
      isDefined(
        matchPath(
          location.pathname,
          `${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BUNDLE_BOOKED_MODAL_PATH}`
        )
      ) ||
      isDefined(
        matchPath(
          location.pathname,
          `${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH}`
        )
      )
    ) {
      void invalidateShifts(queryClient);
    }
  }, [queryClient, location.pathname, baseNavigationPath]);

  return (
    <ModalTransitionSwitch>
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_MODAL_PATH}`}
        render={({ modalState }) => {
          return <ShiftBottomSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_PATH}`}
        render={({ modalState }) => {
          return <ShiftDoubleBottomSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH}`}
        render={({ modalState }) => {
          return <DoubleBookedSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_PATH}`}
        render={({ modalState }) => {
          return <ShiftBundleBottomSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BUNDLE_SELECT_MODAL_PATH}`}
        render={({ modalState }) => {
          return <SelectionSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BUNDLE_BOOKED_MODAL_PATH}`}
        render={({ modalState }) => {
          return <BundleBookedSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <ShiftBookedBottomSheetContainer
              shift={shift}
              modalState={modalState}
              bookabilityStatus={bookabilityStatus}
              computedArrivalData={computedArrivalData}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_EDUCATE_NEW_WORKER_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <EducateNewWorkerBottomSheet
              shift={shift}
              modalState={modalState}
              rateNegotiationId={rateNegotiationId}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_EDUCATE_ABOUT_CLIPBOARD_SCORE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <ClipboardScoreEducationFlowContainer
              shift={shift}
              modalState={modalState}
              rateNegotiationId={rateNegotiationId}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_ARRIVAL_TIME_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            logEvent(APP_V2_APP_EVENTS.SHIFT_ARRIVAL_TIME_BOTTOM_SHEET_ERROR_VIEWED, {
              baseNavigationPath,
              computedArrivalData,
            });
            return null;
          }

          const isWorkerPlacingABid =
            new URLSearchParams(location.search).get("redirectTo") === "place-bid-modal";

          return (
            <ArrivalTimeBottomSheetContainer
              modalState={modalState}
              shift={shift}
              rateNegotiationId={rateNegotiationId}
              isWorkerPlacingABid={isWorkerPlacingABid}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <ConfirmBookingBottomSheetContainer
              shift={shift}
              modalState={modalState}
              rateNegotiationId={rateNegotiationId}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_BASKET_SIZE_WILL_REACH_WARNING_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <BasketSizeWillReachWarning
              modalState={modalState}
              shift={shift}
              rateNegotiationId={rateNegotiationId}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_BREAK_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <BreakConfirmationModalRouteContainer
              shift={shift}
              modalState={modalState}
              rateNegotiationId={rateNegotiationId}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_ENABLE_LOCATION_PERMISSIONS_PATH}`}
        render={({ modalState }) => <LocationPermissionsBottomSheet modalState={modalState} />}
      />

      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={false}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH}`}
        render={({ modalState }) => {
          return <InstantReviewSignFormBottomSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={false}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH}`}
        render={({ modalState }) => {
          return (
            <InstantReviewShiftDocumentSubmittedBottomSheet
              modalState={modalState}
              bookabilityStatus={bookabilityStatus}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          const inputBidPay = new URLSearchParams(location.search).get(
            SHIFT_AUCTION_BID_PAY_PARAMETER_NAME
          );

          return (
            <PlaceBidBottomSheetContainer
              modalState={modalState}
              shift={shift}
              inputBidPay={inputBidPay ? Number(inputBidPay) : undefined}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_AUCTION_REVIEW_BID_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          const inputBidPay = Number(
            new URLSearchParams(location.search).get(SHIFT_AUCTION_BID_PAY_PARAMETER_NAME)
          );

          return (
            <ReviewBidBottomSheetContainer
              modalState={modalState}
              shift={shift}
              inputBidPay={inputBidPay}
            />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_AUCTION_CONFIRM_INSTANT_BOOK_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return (
            <ConfirmAuctionInstantBookBottomSheetContainer modalState={modalState} shift={shift} />
          );
        }}
      />
      <ModalRoute
        closeModalPath={goBackPath}
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_NEGOTIATION_CANCEL_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return <CancelRateNegotiationBottomSheet modalState={modalState} shift={shift} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack={isNestedModalContext}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(shift)) {
            return null;
          }

          return <RateNegotiationBottomSheet modalState={modalState} shift={shift} />;
        }}
      />
    </ModalTransitionSwitch>
  );
}
